import type {
	V2Answers,
	V2Attachment,
	V2Element,
	V2ElementValue,
	V2TableRow,
	V2ValidationRule,
} from '../../types/V2Types';
import { shouldShow } from './showIfEvaluator';

export interface ValidationError {
	elementId: string;
	rule: V2ValidationRule['kind'];
	message: string;
}

export interface ValidationResult {
	valid: boolean;
	errors: ValidationError[];
	/** Convenience lookup by element id for inline error rendering. */
	byElement: Record<string, ValidationError[]>;
}

/**
 * Evaluate a single element against its declared validations and return any
 * violations. Elements that are hidden by their own `showIf` are skipped (a
 * hidden element should never block submission).
 *
 * The evaluator is intentionally permissive: empty optional values pass every
 * rule except `required` itself, so authors can attach loose rules without
 * worrying about firing them on blank inputs.
 */
export function validateElement(
	element: V2Element,
	value: V2ElementValue | undefined,
	answers: V2Answers,
): ValidationError[] {
	if (!shouldShow(element.showIf, answers)) return [];

	const errors: ValidationError[] = [];
	const rules = element.validations ?? [];

	const valueIsEmpty = isEmpty(value);
	const requiredFromRule = rules.some((r) => r.kind === 'required');
	const requiredFromFlag = element.required === true;
	if ((requiredFromRule || requiredFromFlag) && valueIsEmpty && element.type !== 'infoBlock') {
		errors.push({
			elementId: element.elementId,
			rule: 'required',
			message: messageFor(rules.find((r) => r.kind === 'required'), `${element.label} is required.`),
		});
	}

	if (valueIsEmpty) return errors;

	for (const rule of rules) {
		if (rule.kind === 'required') continue;
		const violation = checkRule(element, value as V2ElementValue, rule);
		if (violation) errors.push(violation);
	}

	// Element-level selection bounds (V2MultiSelectElement.maxSelections /
	// minSelections) are authored directly on the element, separate from the
	// `validations[]` list. Enforce them here as implicit bounds so authors can
	// rely on the schema fields without also duplicating them as rules. Skip a
	// bound when an explicit rule of the same kind already exists — the loop
	// above handled that one, and we don't want two identical errors.
	if (element.type === 'multiSelect') {
		const selected = Array.isArray(value) ? value : [];
		const hasRule = (kind: V2ValidationRule['kind']) => rules.some((r) => r.kind === kind);
		if (
			typeof element.maxSelections === 'number' &&
			!hasRule('maxSelections') &&
			selected.length > element.maxSelections
		) {
			errors.push({
				elementId: element.elementId,
				rule: 'maxSelections',
				message: `Please select at most ${element.maxSelections}.`,
			});
		}
		if (
			typeof element.minSelections === 'number' &&
			!hasRule('minSelections') &&
			selected.length < element.minSelections
		) {
			errors.push({
				elementId: element.elementId,
				rule: 'minSelections',
				message: `Please select at least ${element.minSelections}.`,
			});
		}
	}

	return errors;
}

/** Validate every element on a page; returns a combined result. */
export function validatePage(
	elements: V2Element[],
	answers: V2Answers,
): ValidationResult {
	const errors: ValidationError[] = [];
	const byElement: Record<string, ValidationError[]> = {};
	for (const element of elements) {
		const elErrors = validateElement(element, answers[element.elementId], answers);
		if (elErrors.length) {
			errors.push(...elErrors);
			byElement[element.elementId] = elErrors;
		}
	}
	return { valid: errors.length === 0, errors, byElement };
}

function checkRule(
	element: V2Element,
	value: V2ElementValue,
	rule: V2ValidationRule,
): ValidationError | null {
	const fail = (msg: string): ValidationError => ({
		elementId: element.elementId,
		rule: rule.kind,
		message: messageFor(rule, msg),
	});

	switch (rule.kind) {
		case 'minLength': {
			const v = typeof value === 'string' ? value : '';
			return v.length < Number(rule.value)
				? fail(`Must be at least ${rule.value} characters.`)
				: null;
		}
		case 'maxLength': {
			const v = typeof value === 'string' ? value : '';
			return v.length > Number(rule.value) ? fail(`Must be at most ${rule.value} characters.`) : null;
		}
		case 'pattern': {
			const v = typeof value === 'string' ? value : '';
			try {
				return new RegExp(String(rule.value)).test(v) ? null : fail('Value is not in the expected format.');
			} catch {
				return null;
			}
		}
		case 'minValue': {
			const n = toNumber(value);
			return Number.isFinite(n) && n < Number(rule.value)
				? fail(`Must be at least ${rule.value}.`)
				: null;
		}
		case 'maxValue': {
			const n = toNumber(value);
			return Number.isFinite(n) && n > Number(rule.value)
				? fail(`Must be at most ${rule.value}.`)
				: null;
		}
		case 'minDate': {
			const v = typeof value === 'string' ? value : '';
			return v && v < String(rule.value) ? fail(`Must be on or after ${rule.value}.`) : null;
		}
		case 'maxDate': {
			const v = typeof value === 'string' ? value : '';
			return v && v > String(rule.value) ? fail(`Must be on or before ${rule.value}.`) : null;
		}
		case 'fileMaxBytes': {
			const files = (Array.isArray(value) ? value : []) as V2Attachment[];
			const limit = Number(rule.value);
			return files.some((f) => f.size > limit)
				? fail(`Each file must be ${limit} bytes or smaller.`)
				: null;
		}
		case 'fileMimeTypes': {
			const allowed = Array.isArray(rule.value) ? (rule.value as string[]) : [];
			const files = (Array.isArray(value) ? value : []) as V2Attachment[];
			return files.some((f) => f.mimeType && !allowed.includes(f.mimeType))
				? fail(`Allowed file types: ${allowed.join(', ')}.`)
				: null;
		}
		case 'minSelections': {
			const arr = Array.isArray(value) ? value : [];
			return arr.length < Number(rule.value)
				? fail(`Please select at least ${rule.value}.`)
				: null;
		}
		case 'maxSelections': {
			const arr = Array.isArray(value) ? value : [];
			return arr.length > Number(rule.value) ? fail(`Please select at most ${rule.value}.`) : null;
		}
		case 'minRows': {
			const rows = (Array.isArray(value) ? value : []) as V2TableRow[];
			return rows.length < Number(rule.value) ? fail(`Please add at least ${rule.value} rows.`) : null;
		}
		case 'maxRows': {
			const rows = (Array.isArray(value) ? value : []) as V2TableRow[];
			return rows.length > Number(rule.value) ? fail(`Please add at most ${rule.value} rows.`) : null;
		}
		default:
			return null;
	}
}

function isEmpty(value: V2ElementValue | undefined): boolean {
	if (value === undefined || value === null) return true;
	if (typeof value === 'string') return value.trim().length === 0;
	if (typeof value === 'boolean') return value === false;
	if (Array.isArray(value)) return value.length === 0;
	return false;
}

function toNumber(value: unknown): number {
	if (typeof value === 'number') return value;
	if (typeof value === 'string' && value.trim() !== '') {
		const n = Number(value);
		return Number.isFinite(n) ? n : Number.NaN;
	}
	return Number.NaN;
}

function messageFor(rule: V2ValidationRule | undefined, fallback: string): string {
	return rule?.message?.trim() ? rule.message : fallback;
}
